import { useState, useEffect } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import API from '../../services/apiService'
import { useConfirm } from '../ConfirmModal/ConfirmModal'
import { Skeleton } from '../Skeleton/Skeleton'
import './AnnouncementsManager.css'

const AnnounceIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18">
    <defs>
      <linearGradient id="annGr" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#93c5fd" />
        <stop offset="100%" stopColor="#3b82f6" />
      </linearGradient>
    </defs>
    <path d="M18 8a6 6 0 010 8M6 8a6 6 0 000 8M4 12h16" stroke="url(#annGr)" strokeWidth="2" fill="none" strokeLinecap="round"/>
    <rect x="8" y="3" width="8" height="18" rx="2" fill="url(#annGr)" stroke="#1e40af" strokeWidth="1"/>
    <path d="M10 8h4M10 12h4M10 16h2" stroke="#1e3a8a" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

const formatDate = (d) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const typeColors = {
  event: '#f59e0b',
  update: '#22c55e',
  maintenance: '#ef4444',
  news: '#3b82f6',
  alert: '#ec4899',
}

export default function AnnouncementsManager() {
  const { t } = useTranslation()
  const [announcements, setAnnouncements] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [editing, setEditing] = useState(null)
  const [showAll, setShowAll] = useState(false)
  const [form, setForm] = useState({
    title: '', content: '', type: 'news', isActive: true, priority: 0,
    publishedAt: new Date().toISOString().slice(0, 16), expiresAt: ''
  })
  const confirm = useConfirm()

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await API.Announcements.list(1, 50, !showAll)
      setAnnouncements(Array.isArray(res.data) ? res.data : [])
    } catch (err) {
      setError(err.message || t('admin.annLoadFail'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [showAll])

  const handleSave = async () => {
    if (!form.title?.trim() || !form.content?.trim()) {
      setError(t('admin.annTitleContentRequired')); return
    }
    setError(null); setSuccess(null)
    const payload = {
      ...form,
      publishedAt: form.publishedAt ? new Date(form.publishedAt).toISOString() : null,
      expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
    }
    try {
      if (editing) {
        await API.Announcements.update(editing, payload)
        setSuccess(t('admin.annUpdateSuccess'))
      } else {
        await API.Announcements.create(payload)
        setSuccess(t('admin.annCreateSuccess'))
      }
      setEditing(null)
      setForm({ title: '', content: '', type: 'news', isActive: true, priority: 0, publishedAt: new Date().toISOString().slice(0, 16), expiresAt: '' })
      fetchData()
    } catch (err) {
      setError(err.message || t('admin.annSaveFail'))
    }
  }

  const handleDelete = async (id, title) => {
    if (!await confirm(t('admin.annDeleteConfirm', { title }), t('admin.annDeleteUndoable'))) return
    try {
      await API.Announcements.delete(id)
      setSuccess(t('admin.annDeleteSuccess', { title }))
      fetchData()
    } catch (err) {
      setError(err.message || t('admin.annDeleteFail'))
    }
  }

  const startEdit = (a) => {
    setEditing(a.id)
    setForm({
      title: a.title || '',
      content: a.content || '',
      type: a.type || 'news',
      isActive: a.isActive !== false,
      priority: a.priority || 0,
      publishedAt: a.publishedAt ? new Date(a.publishedAt).toISOString().slice(0, 16) : '',
      expiresAt: a.expiresAt ? new Date(a.expiresAt).toISOString().slice(0, 16) : '',
    })
  }

  if (loading) return (
    <div className="ann-manager">
      <div className="ann-manager__header"><h3><AnnounceIcon /> {t('admin.news')}</h3></div>
      <Skeleton height="120px" />
    </div>
  )

  return (
    <div className="ann-manager">
      <div className="ann-manager__header">
        <h3><AnnounceIcon /> {t('admin.news')}</h3>
        <label className="ann-manager__toggle">
          <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
          {t('admin.showInactive')}
        </label>
      </div>

      {error && <div className="ann-manager__error">{error}</div>}
      {success && <div className="ann-manager__success">{success}</div>}

      <div className="ann-manager__form">
        <h4>{editing ? t('admin.editAnnouncement') : t('admin.createAnnouncement')}</h4>
        <div className="ann-manager__form-fields">
          <input placeholder={t('admin.title') + ' *'} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="ann-manager__input" />
          <textarea placeholder={t('admin.content') + ' *'} value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} className="ann-manager__textarea" rows={3} />
          <div className="ann-manager__form-row">
            <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="ann-manager__select">
              <option value="news">{t('announcements.latestNews')}</option>
              <option value="event">{t('account.serverStatus')}</option>
              <option value="update">{t('account.updatePatch')}</option>
              <option value="maintenance">{t('server.maintenance')}</option>
              <option value="alert">{t('admin.alert')}</option>
            </select>
            <input type="number" placeholder={t('admin.priority')} value={form.priority} onChange={e => setForm(p => ({ ...p, priority: +e.target.value }))} className="ann-manager__input ann-manager__input--small" />
            <label className="ann-manager__checkbox-label">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} />
              {t('admin.gmActive')}
            </label>
          </div>
          <div className="ann-manager__form-row">
            <label className="ann-manager__date-label">
              {t('admin.publishDate')}
              <input type="datetime-local" value={form.publishedAt} onChange={e => setForm(p => ({ ...p, publishedAt: e.target.value }))} className="ann-manager__input" />
            </label>
            <label className="ann-manager__date-label">
              {t('admin.expirationDate')}
              <input type="datetime-local" value={form.expiresAt} onChange={e => setForm(p => ({ ...p, expiresAt: e.target.value }))} className="ann-manager__input" />
            </label>
          </div>
        </div>
        <div className="ann-manager__form-actions">
          <button className="ann-manager__btn ann-manager__btn--save" onClick={handleSave}>
            {editing ? t('admin.editAnnouncement') : t('admin.createAnnouncement')}
          </button>
          {editing && (
            <button className="ann-manager__btn ann-manager__btn--cancel" onClick={() => {
              setEditing(null)
              setForm({ title: '', content: '', type: 'news', isActive: true, priority: 0, publishedAt: new Date().toISOString().slice(0, 16), expiresAt: '' })
            }}>{t('account.cancel')}</button>
          )}
        </div>
      </div>

      <div className="ann-manager__list">
        {announcements.length === 0 ? (
          <p className="ann-manager__empty">{t('admin.noData')}</p>
        ) : (
          announcements.map(a => (
            <div key={a.id} className={`ann-manager__item ${!a.isActive ? 'ann-manager__item--inactive' : ''}`}>
              <div className="ann-manager__item-indicator" style={{ background: typeColors[a.type] || '#3b82f6' }} />
              <div className="ann-manager__item-info">
                <div className="ann-manager__item-header">
                  <span className="ann-manager__item-type" style={{ color: typeColors[a.type] }}>{a.type}</span>
                  <strong>{a.title}</strong>
                  <span className="ann-manager__item-priority">P{a.priority}</span>
                  {!a.isActive && <span className="ann-manager__item-badge ann-manager__item-badge--inactive">{t('admin.gmInactive')}</span>}
                  {a.expiresAt && new Date(a.expiresAt) < new Date() && <span className="ann-manager__item-badge ann-manager__item-badge--expired">{t('account.expired')}</span>}
                </div>
                <p className="ann-manager__item-content">{a.content}</p>
                <div className="ann-manager__item-meta">
                  <span>{t('admin.publishDate')}: {formatDate(a.publishedAt)}</span>
                  {a.expiresAt && <span>{t('admin.expirationDate')}: {formatDate(a.expiresAt)}</span>}
                  {a.createdByAdminName && <span>{t('admin.modifiedBy')} {a.createdByAdminName}</span>}
                </div>
              </div>
              <div className="ann-manager__item-actions">
                <button className="ann-manager__btn ann-manager__btn--edit" onClick={() => startEdit(a)}>{t('admin.editAnnouncement')}</button>
                <button className="ann-manager__btn ann-manager__btn--delete" onClick={() => handleDelete(a.id, a.title)}>{t('admin.deleteAnnouncement')}</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
